import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Spin, message, Space } from "antd";
import { FilePdfOutlined, CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Method_POST_CONTEXTO } from "../../../../service/api";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

/* ======================================================
   UTIL - ENVIO DE DATA (SEGURANÇA CONTRA TIMEZONE)
   ====================================================== */
const formatDateSafe = (date) =>
  dayjs(date).hour(12).minute(0).second(0).toISOString();

export default function BalancoModal({ visible, periodo, onClose, onOpenInforme, title, zIndex, onFocus }) {
  const draggableProps = useDraggableModal(zIndex, onFocus, {
    top: 20,
    initialWidth: 1200,
    initialHeight: 820,
    modalKey: 'balanco',
    visible: visible
  });
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  const company = sessionStorage.getItem("COMPANY") ? JSON.parse(sessionStorage.getItem("COMPANY")) : null;
  const clienteId = company?.id || 110;

  /* ======================================================
     DECODIFICADOR BASE64 ROBUSTO (CORRIGE INVALID CHARACTER ERROR)
     ====================================================== */
  const base64ToBlob = (base64, mime) => {
    try {
      // 1. Remove qualquer cabeçalho (data:application/pdf;base64,)
      const base64WithoutHeader = base64.includes(",") ? base64.split(",")[1] : base64;

      // 2. Limpa espaços e quebras de linha que corrompem o atob
      const cleanBase64 = base64WithoutHeader.replace(/\s/g, "");

      const binary = atob(cleanBase64);
      const buffer = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        buffer[i] = binary.charCodeAt(i);
      }
      return new Blob([buffer], { type: mime });
    } catch (e) {
      console.error("Falha na decodificação do PDF:", e);
      throw new Error("Base64 inválido");
    }
  };

  /* ======================================================
     GERAR PREVIEW
     ====================================================== */
  const gerarPreview = useCallback(async () => {
    setLoading(true);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl); // Limpeza de memória
    setPdfUrl("");

    try {
      const storedPeriodo = sessionStorage.getItem("periodo");
      const period = storedPeriodo ? JSON.parse(storedPeriodo).map((d) => dayjs(d)) : periodo;

      if (!period || period.length < 2) throw new Error("Período não selecionado");

      const payload = {
        dataInicio: formatDateSafe(period[0]),
        dataTermino: formatDateSafe(period[1]),
        cliente: { id: clienteId },
      };

      const response = await Method_POST_CONTEXTO(
        "/empresa/contabilidade/create/temporary/balanco/patrimonial",
        payload
      );

      // Trata se o retorno for objeto { data: "..." } ou string pura
      const base64String = typeof response === 'object' ? response.data : response;

      if (!base64String || base64String.length < 100) {
        throw new Error("PDF vazio vindo do servidor.");
      }

      const blob = base64ToBlob(base64String, "application/pdf");
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

    } catch (err) {
      console.error(err);
      message.error("Não foi possível carregar a visualização do balanço.");
    } finally {
      setLoading(false);
    }
  }, [clienteId, periodo]);

  useEffect(() => {
    if (visible) gerarPreview();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [visible]);

  /* ======================================================
     CONFIRMAR BALANÇO (LÓGICA DE BLOQUEIO)
     ====================================================== */
  const confirmarBalanco = async () => {
    setLoading(true);
    try {
      const storedPeriodo = sessionStorage.getItem("periodo");
      const period = storedPeriodo ? JSON.parse(storedPeriodo).map((d) => dayjs(d)) : periodo;

      const payload = {
        dataInicio: formatDateSafe(period[0]),
        dataTermino: formatDateSafe(period[1]),
        cliente: { id: clienteId },
      };

      await Method_POST_CONTEXTO("/empresa/contabilidade/create/balanco/patrimonial", payload);

      message.success("Balanço processado! Bloqueando período...");

      // Bloqueia meses do período
      let cursor = dayjs(payload.dataInicio).startOf("month");
      const fim = dayjs(payload.dataTermino).endOf("month");
      while (cursor.isSameOrBefore(fim, "month")) {
        await Method_POST_CONTEXTO(`/empresa/contabilidade/bloquear/periodo?ano=${cursor.year()}&mes=${cursor.month() + 1}`);
        cursor = cursor.add(1, "month");
      }

      message.success("Período bloqueado com sucesso.");
      onClose();
      onOpenInforme?.();
    } catch (err) {
      message.error("Erro na confirmação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={title || <Space><FilePdfOutlined /> Balanço Patrimonial</Space>}
      open={visible}
      onCancel={onClose}
      width="1200"
      className="premium-modal-v2"
      {...draggableProps}
      styles={{
        content: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        },
        body: {
          flex: 1,
          padding: 4,
          overflow: "hidden",
          background: '#525659',
          height: '80vh'
        }
      }}
      footer={[
        <Button key="close" onClick={onClose}>Fechar</Button>,
        (periodo && periodo.length === 2 &&
          periodo[0].month() === 0 &&
          periodo[1].month() === 11 &&
          periodo[0].year() === periodo[1].year()) && (
          <Button key="confirm" type="primary" icon={<CheckCircleOutlined />} loading={loading} onClick={confirmarBalanco}>
            Confirmar e Fechar Ano
          </Button>
        )
      ]}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", background: '#525659' }}>
          <Spin size="large" />
        </div>
      ) : (
        pdfUrl && (
          <iframe
            key={pdfUrl}
            src={pdfUrl + "#zoom=80"}
            title="PDF"
            width="100%"
            height="100%"
            style={{ border: "none", display: 'block' }}
          />
        )
      )}
    </Modal>
  );
}